import { Cores } from 'src/componentes/styles/cores'

/**
 * Status da condição especial (matrícula negociada).
 * Espelha o enum `StatusProposta` do backend
 * (Ipgs.Educacional.Domain/Enums/StatusProposta.cs). Os valores string batem
 * com os nomes do enum C# — que é como o backend serializa/aceita.
 */
export enum StatusCondicaoEspecial {
  Iniciada = 'Iniciada',
  EmNegociacao = 'EmNegociacao',
  EnviadaAluno = 'EnviadaAluno',
  EmAndamento = 'EmAndamento',
  FinalizadaAluno = 'FinalizadaAluno',
  EntradaPaga = 'EntradaPaga',
  Finalizada = 'Finalizada',
  Cancelada = 'Cancelada',
  Expirada = 'Expirada'
}

/** Rótulos iguais aos [Description] do enum no backend. */
export enum StatusCondicaoEspecialPorExtenso {
  Iniciada = 'Iniciada',
  EmNegociacao = 'Em negociação comercial',
  EnviadaAluno = 'Enviada ao aluno',
  EmAndamento = 'Em andamento pelo aluno',
  FinalizadaAluno = 'Finalizada pelo aluno',
  EntradaPaga = 'Entrada paga',
  Finalizada = 'Finalizada',
  Cancelada = 'Cancelada',
  Expirada = 'Expirada'
}

/** Cor de fundo do badge de status na listagem/detalhe. */
export const CorStatusCondicaoEspecial: Record<
  keyof typeof StatusCondicaoEspecial,
  { fundo: string; texto: string }
> = {
  Iniciada: { fundo: Cores.CINZA_3, texto: Cores.CINZA_1 },
  EmNegociacao: { fundo: Cores.ATENCAO_FUNDO, texto: Cores.ATENCAO_ESCURO },
  EnviadaAluno: { fundo: Cores.ATENCAO_FUNDO, texto: Cores.ATENCAO_ESCURO },
  // O aluno abriu o link e reservou a matrícula, mas ainda não aceitou o contrato.
  EmAndamento: { fundo: Cores.ATENCAO_FUNDO, texto: Cores.ATENCAO_ESCURO },
  FinalizadaAluno: { fundo: Cores.PRIMARIA_1_36, texto: Cores.PRIMARIA_1 },
  EntradaPaga: { fundo: Cores.PRIMARIA_1_36, texto: Cores.PRIMARIA_1 },
  Finalizada: { fundo: Cores.SUCESSO_FUNDO, texto: Cores.SUCESSO_ESCURO },
  Cancelada: {
    fundo: Cores.PERIGO_ERRO_FUNDO,
    texto: Cores.PERIGO_ERRO_ESCURO
  },
  Expirada: { fundo: Cores.PERIGO_ERRO_FUNDO, texto: Cores.PERIGO_ERRO_ESCURO }
}

/**
 * Status que o consultor pode definir à mão. Os demais dependem de pagamento
 * confirmado ou de ação do aluno — o backend recusa a alteração manual deles
 * (PropostaMatricula.AlterarStatusManualmente).
 */
export const StatusCondicaoEspecialEditaveis = [
  StatusCondicaoEspecial.EmNegociacao,
  StatusCondicaoEspecial.EnviadaAluno
]

/**
 * Destino de uma opção de pagamento dentro do plano negociado.
 * Espelha `DestinoFormaPagamento` no backend.
 */
export enum DestinoFormaPagamento {
  Entrada = 'Entrada',
  Restante = 'Restante'
}

export enum DestinoFormaPagamentoPorExtenso {
  Entrada = 'Entrada',
  Restante = 'Restante'
}

/** Piso da tolerância, para poucas parcelas. Espelha o backend. */
const TOLERANCIA_MINIMA = 0.05

/** Maior erro de arredondar UMA parcela para o centavo: meio centavo. */
const ERRO_MAXIMO_POR_PARCELA = 0.005

/**
 * Resíduo aceito entre `quantidadeParcelas x valorParcela` e `valor`.
 * Espelha PlanoPagamentoNegociado.ToleranciaDeArredondamento — a regra é a mesma
 * nos dois lados, e o backend é a autoridade.
 *
 * Proporcional à quantidade porque o erro de arredondar cada parcela soma até
 * meio centavo por parcela: 7.700 em 21x dá 21 x 366,67 = 7.700,07, que estourava
 * o limite fixo de R$ 0,05 sem haver nada de errado nos valores.
 *
 * O `+ 0.0001` absorve o ruído de ponto flutuante do JS (21 * 366.67 não é
 * exatamente 7700.07), que no backend, em `decimal`, não existe.
 */
export const toleranciaDeArredondamento = (quantidadeParcelas: number): number =>
  Math.max(TOLERANCIA_MINIMA, quantidadeParcelas * ERRO_MAXIMO_POR_PARCELA) +
  0.0001
